import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFeaturedRestaurantApi } from '../../../Hooks/useFeaturedRestaurantApi';
import { usePublicSettings } from '../../../Hooks/usePublicSettings';
import {
  Box,
  Paper,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Container,
  Card,
  CardContent,
  alpha,
  Divider,
  Alert
} from '@mui/material';
import { COLORS } from '../../../theme/themeConstants';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
const MakeyourRestaurant = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [, forceUpdate] = useState({});
  const { calculateFeaturedPrice, purchaseFeaturedStore, loading, error } = useFeaturedRestaurantApi();
  const { featuredRestaurantEnabled, loading: settingsLoading } = usePublicSettings();
  
  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const storeId = userData?.userDetails?.storeIds[0];
  const merchantId = userData?.userId;

  // Redirect if featured restaurant is disabled
  useEffect(() => {
    if (!settingsLoading && !featuredRestaurantEnabled) {
      navigate('/layout/dashboard');
    }
  }, [featuredRestaurantEnabled, settingsLoading, navigate]);

  console.log('User Data:', storeId, merchantId);
  
  const [fromDate, setFromDate] = useState<Dayjs | null>(() => dayjs().subtract(1, 'year'));
  const [toDate, setToDate] = useState<Dayjs | null>(() => dayjs().subtract(1, 'year'));
  const [days, setDays] = useState(0);
  const [pricePerDay, setPricePerDay] = useState(40);
  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<"stripe" | "hyperpay">("stripe");
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleDateChange = async (type: "from" | "to", value: Dayjs | null) => {
    setPurchaseError(null);
  
    if (!value) {
      if (type === "from") setFromDate(null);
      else setToDate(null);
      return;
    }
  
    // Update state
    if (type === "from") setFromDate(value);
    else setToDate(value);
  
    // Only calculate price if both dates are selected
    const fromDateToUse = type === "from" ? value : fromDate;
    const toDateToUse = type === "to" ? value : toDate;
  
    if (!fromDateToUse || !toDateToUse) return;
  
    // Start and end of day using Dayjs
    const today = dayjs().startOf('day');
    const start = fromDateToUse.startOf('day');
    const end = toDateToUse.endOf('day');
  
    // Validation
    if (start.isBefore(today)) {
      console.warn('From date cannot be in the past');
      return;
    }
    if (end.isBefore(start)) {
      console.warn('To date cannot be before from date');
      return;
    }
  
    try {
      // Convert Dayjs to ISO strings for API
      const fromDateISO = start.toISOString();
      const toDateISO = end.toISOString();
  
      const response = await calculateFeaturedPrice({
        fromDate: fromDateISO,
        toDate: toDateISO,
      });
  
      setDays(response.numberOfDays);
      setPricePerDay(response.pricePerDay);
      setTotalPrice(response.totalPrice);
    } catch (err) {
      console.error('Error calculating price:', err);
  
      // Fallback calculation
      const diff = end.diff(start, 'day') + 1;
      setDays(diff > 0 ? diff : 0);
      setTotalPrice(diff > 0 ? diff * pricePerDay : 0);
    }
  };
  
  

  const handleSubmit = async () => {
    // Clear previous errors
    setPurchaseError(null);
    
    // Validate required fields
    if (!fromDate || !toDate) {
      setPurchaseError(t('featuredRestaurant.purchaseError1'));
      ;
      return;
    }

    if (!selectedPaymentMethod) {
      setPurchaseError(t('featuredRestaurant.purchaseError2'));
      return;
    }

    if (!storeId || !merchantId) {
      setPurchaseError(t('featuredRestaurant.purchaseError3'));
      return;
    }

    try {
      const purchaseRequest = {
        storeId,
        merchantId,
        fromDate: `${fromDate}T00:00:00.000Z`,
        toDate: `${toDate}T23:59:59.999Z`,
        paymentMethod: selectedPaymentMethod,
        platform: 'web' as const,
        currencyCode: 'JOD'
      };
      console.log('Purchase Request:', purchaseRequest);
      const response = await purchaseFeaturedStore(purchaseRequest) as any;

      response.customParameters = {subscriptionId : response.subscriptionId, merchantId: merchantId};
      
      // Handle successful purchase
      if (selectedPaymentMethod === 'hyperpay') {
        // For HyperPay, navigate to the wizard page with checkoutId
        if (response.checkoutId) {
          console.log('Redirecting to HyperPay wizard page:', response.checkoutId);
          
          // Save to localStorage as backup
          try {
            localStorage.setItem('hyperpay_checkout_id', response.checkoutId);
            localStorage.setItem('hyperpay_merchant_id', merchantId);
            localStorage.setItem('hyperpay_subscription_id', response.subscriptionId || '');
          } catch (err) {
            console.error('Failed to save to localStorage:', err);
          }
          
          navigate(`/payment/featured-store/hyperpay/wizard?checkoutId=${response.checkoutId}&subscriptionId=${response.subscriptionId || ''}&merchantId=${merchantId}`);
        } else {
          setPurchaseError(t('Failed to get checkout ID from server'));
        }
      } else if (selectedPaymentMethod === 'stripe') {
        // For Stripe, redirect to checkout URL
        if (response.checkoutUrl) {
          console.log('Redirecting to Stripe checkout URL:', response.checkoutUrl);
          window.location.href = response.checkoutUrl;
        } else {
          setPurchaseError(
            t('featuredRestaurant.purchaseError5', {
              subscriptionId: response.subscriptionId,
            })
          );
                  }
      }
      
    } catch (err) {
      console.error('Purchase failed:', err);
      
      // Extract error message from backend response
      let errorMessage = 'Purchase failed. Please try again.';
      
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
        if (axiosError.response?.data?.message) {
          // Backend returned a specific error message
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.message) {
          // General error message
          errorMessage = axiosError.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setPurchaseError(errorMessage);
    }
  };

  // Don't render if settings are loading or feature is disabled
  if (settingsLoading || !featuredRestaurantEnabled) {
    return null;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)',
      py: 4
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1
          }}>
            {t('featuredRestaurant.title')}
          </Typography>
          <Typography variant="body1" sx={{
            color: 'text.secondary',
            opacity: 0.8
          }}>
            {t('featuredRestaurant.header.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {/* Left: Form Card */}
          <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
            <Paper sx={{
              p: 4,
              background: '#ffffff',
              border: '1px solid #e8eaed',
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
              {/* Price Display */}
              <Card sx={{
                mb: 4,
                background: `linear-gradient(135deg, ${COLORS.primary.main} 0%, ${alpha(COLORS.primary.main, 0.8)} 100%)`,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${alpha(COLORS.primary.main, 0.2)}`
              }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="caption" sx={{
                    color: COLORS.secondary.main,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    display: 'block',
                    mb: 1
                  }}>
                    {t('featuredRestaurant.pricing.perDay')}
                  </Typography>
                  <Typography variant="h2" sx={{
                    color: COLORS.secondary.main,
                    fontWeight: 700
                  }}>
                    JOD{pricePerDay.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>

              {/* Date Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary'
                }}>
                  {t('featuredRestaurant.form.selectDuration')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 1, fontSize: '0.875rem' }}>
                        {t('featuredRestaurant.form.fromDate')}
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
    value={fromDate}
    onChange={(newValue) => handleDateChange('from', newValue)}
    format="MM/DD/YYYY"
    slotProps={{
      textField: {
        size: 'small',
        sx: {
          width: 230,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.primary.main,
            },
          },
        },
      },
    }}
  />
                                            </LocalizationProvider>

                    </FormControl>
                  </Box>
                  <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 1, fontSize: '0.875rem' }}>
                        {t('featuredRestaurant.form.toDate')}
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
    value={toDate}
    onChange={(newValue) => handleDateChange('to', newValue)}
    format="MM/DD/YYYY"
    slotProps={{
      textField: {
        size: 'small',
        sx: {
          width: 230,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.primary.main,
            },
          },
        },
      },
    }}
  />
                                            </LocalizationProvider>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              {/* Total Price Display */}
              <Box sx={{
                p: 3,
                mb: 3,
                background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%)',
                borderRadius: 2,
                border: '1px solid #e8eaed'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('featuredRestaurant.pricing.totalPrice')}
                  </Typography>
                  <Typography variant="h4" sx={{
                    fontWeight: 700,
                    color: COLORS.secondary.main
                  }}>
                    JOD{loading ? "..." : totalPrice.toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {loading ? t('common.calculating') : `${days} ${t('featuredRestaurant.pricing.days')}`}
                </Typography>
              </Box>

              {/* Error Display - Shows both calculation and purchase errors */}
              {(purchaseError || error) && (
                <Alert 
                  severity="error" 
                  onClose={() => {
                    setPurchaseError(null);
                  }}
                  sx={{ mb: 3 }}
                >
                  {purchaseError || error}
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Payment Methods */}
              <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary'
                }}>
                  {t('featuredRestaurant.form.paymentMethods')}
                </FormLabel>
                <RadioGroup
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value as "stripe" | "hyperpay")}
                >
                  {/* <Paper sx={{
                    p: 2.5,
                    mb: 1.5,
                    border: selectedPaymentMethod === "stripe" ? `2px solid ${COLORS.primary.main}` : '1px solid #e8eaed',
                    borderRadius: 2,
                    background: selectedPaymentMethod === "stripe" ? alpha(COLORS.primary.main, 0.03) : '#ffffff',
                    transition: 'all 0.2s',
                    minHeight: 72,
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      borderColor: COLORS.primary.main,
                      boxShadow: `0 4px 12px ${alpha(COLORS.primary.main, 0.12)}`
                    }
                    }}>
                    <FormControlLabel
                      value="stripe"
                      control={<Radio sx={{ 
                        color: COLORS.primary.main,
                        '&.Mui-checked': { color: COLORS.primary.main },
                        '& .MuiSvgIcon-root': { fontSize: 24 }
                      }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: selectedPaymentMethod === "stripe" ? 600 : 400 }}>
                            Stripe
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper> */}
                  <Paper sx={{
                    p: 2.5,
                    border: selectedPaymentMethod === "hyperpay" ? `2px solid ${COLORS.primary.main}` : '1px solid #e8eaed',
                    borderRadius: 2,
                    background: selectedPaymentMethod === "hyperpay" ? alpha(COLORS.primary.main, 0.03) : '#ffffff',
                    transition: 'all 0.2s',
                    minHeight: 72,
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      borderColor: COLORS.primary.main,
                      boxShadow: `0 4px 12px ${alpha(COLORS.primary.main, 0.12)}`
                    }
                  }}>
                    <FormControlLabel
                      value="hyperpay"
                      control={<Radio sx={{ 
                        color: COLORS.primary.main,
                        '&.Mui-checked': { color: COLORS.primary.main },
                        '& .MuiSvgIcon-root': { fontSize: 24 }
                      }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: selectedPaymentMethod === "hyperpay" ? 600 : 400 }}>
                            {t('payments.hyperpay')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </RadioGroup>
              </FormControl>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={loading || !fromDate || !toDate || !selectedPaymentMethod}
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderRadius: 2,
                  background: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    background: alpha(COLORS.primary.main, 0.9),
                    boxShadow: `0 6px 14px ${alpha(COLORS.primary.main, 0.22)}`
                  },
                  '&:disabled': {
                    background: '#cbd5e1',
                    color: '#ffffff'
                  }
                }}
              >
                {loading ? t('common.processing') : t('featuredRestaurant.actions.purchase')}
              </Button>
            </Paper>
          </Box>

          {/* Right: Info Panel */}
          <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
            <Paper sx={{
              p: 4,
              background: '#ffffff',
              border: '1px solid #e8eaed',
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              height: '100%'
            }}>
              <Box sx={{
                display: 'inline-block',
                px: 2.5,
                py: 1,
                background: COLORS.primary.main,
                color: COLORS.secondary.main,
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 600,
                mb: 3,
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
                {t('featuredRestaurant.info.title')}
              </Box>

              <Typography variant="h5" sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary'
              }}>
                {t('featuredRestaurant.info.subtitle')}
              </Typography>

              <Typography variant="body1" sx={{
                color: 'text.secondary',
                mb: 4,
                lineHeight: 1.7
              }}>
                {t('featuredRestaurant.info.description')}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Features List */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  {t('featuredRestaurant.benefits.title')}
                </Typography>
                {[
                  { title: t('featuredRestaurant.benefits.items.increasedVisibility.title'), desc: t('featuredRestaurant.benefits.items.increasedVisibility.desc') },
                  { title: t('featuredRestaurant.benefits.items.targetedReach.title'), desc: t('featuredRestaurant.benefits.items.targetedReach.desc') },
                  { title: t('featuredRestaurant.benefits.items.instantBoost.title'), desc: t('featuredRestaurant.benefits.items.instantBoost.desc') },
                  { title: t('featuredRestaurant.benefits.items.moreOrders.title'), desc: t('featuredRestaurant.benefits.items.moreOrders.desc') }
                ].map((feature, idx) => (
                  <Box key={idx} sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 2,
                    p: 2,
                    background: '#fafbfc',
                    borderRadius: 2,
                    border: '1px solid #e8eaed'
                  }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: COLORS.primary.main,
                      mt: 1
                    }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{
                p: 3,
                background: alpha(COLORS.primary.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(COLORS.primary.main, 0.2)}`,
                textAlign: 'center'
              }}>
                <Typography variant="subtitle1" sx={{
                  fontWeight: 700,
                  color: COLORS.primary.main,
                  mb: 1
                }}>
                  {t('featuredRestaurant.info.getStarted')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('featuredRestaurant.info.hint')}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MakeyourRestaurant;